import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  Tooltip,
  Dialog,
  IconButton,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import * as actions from "./actions";
import { API_URL } from "../../constants";
import _ from "lodash";
import { toastError, copyToClipboard } from "../../utils";
import SearchInput from "../../components/SearchInput";
import EmptyState from "../../components/EmptyState";
import { Megaphone, User, Mail, Music, Monitor, ListMusic, ExternalLink, Calendar, Image, Copy, ClipboardCheck, Link2, X } from 'lucide-react';

function CopyMeta({ icon, label, value, copyValue }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(copyValue || value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function Promotions(props) {
  const [count, setCount] = useState(0);
  const [promo, setPromo] = useState([]);
  const [filters, setFilters] = useState({
    limit: 30,
    offset: 0,
    page: 1,
    search: "",
  });
  const [previewImage, setPreviewImage] = useState(null);

  const delaySearch = useCallback(
    _.debounce((filters) => getPromotions(filters), 500),
    []
  );

  useEffect(() => {
    document.title = 'PM | Promotions';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    getPromotions(filters);
  }, []);

  const getPromotions = (filters) => {
    props.setLoader(true);
    actions
      .getPromotionsAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setPromo(res.body || []);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    getPromotions(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const renderPromoCard = (item) => (
    <Paper
      key={item.promotion_id}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        {item.image ? (
          <img
            src={`${API_URL}/${item.image}`}
            alt=""
            className="approved-release-card__artwork-img"
            style={{ cursor: 'pointer' }}
            onClick={() => setPreviewImage(`${API_URL}/${item.image}`)}
          />
        ) : (
          <div className="approved-release-card__artwork-fallback">
            <Megaphone size={20} color="#64748B" />
            <span>Promo</span>
          </div>
        )}
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.song || 'Untitled Song'}
            </Typography>
            {item.platform && (
              <Chip
                label={item.platform}
                size="small"
                sx={{
                  backgroundColor: 'rgba(108, 99, 255, 0.12)',
                  color: '#A78BFA',
                  border: '1px solid rgba(108, 99, 255, 0.2)',
                  fontWeight: 500,
                  fontSize: '0.72rem',
                  height: 22,
                  ml: 1,
                }}
              />
            )}
            {item.type && (
              <Chip
                label={item.type}
                size="small"
                sx={{
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10B981',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  fontWeight: 500,
                  fontSize: '0.72rem',
                  height: 22,
                  ml: 0.5,
                }}
              />
            )}
          </div>
          {/* Link + Date on top right */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
            {item.playlist_link && (
              <>
                <Link2 size={14} style={{ display: 'block', color: '#6C63FF' }} />
                <CopyMeta icon={null} label="Playlist Link" value="Copy" copyValue={item.playlist_link} />
                <Tooltip title="Open in new tab" arrow placement="top">
                  <a
                    href={item.playlist_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#6C63FF', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    onClick={e => e.stopPropagation()}
                  >
                    <ExternalLink size={13} /> Open
                  </a>
                </Tooltip>
                <span style={{ color: 'var(--border)', margin: '0 2px' }}>|</span>
              </>
            )}
            {item.created_at && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: '#F59E0B', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                <Calendar size={13} style={{ color: '#F59E0B' }} /> {item.created_at}
              </span>
            )}
          </span>
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-P-{item.promotion_id}</span>
          <CopyMeta icon={<User size={13} />} label="Name" value={item.name} />
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          {item.playlist_name && (
            <CopyMeta icon={<ListMusic size={13} />} label="Playlist" value={item.playlist_name} />
          )}
        </div>
      </div>
    </Paper>
  );

  return (
    <Grid container>
      {/* ========== Header ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
          <Box sx={{ maxWidth: 420, flex: 1 }}>
            <SearchInput
              value={filters.search}
              onChange={onChangeSearch}
              placeholder="Search by name, email, or song..."
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
            <Chip
              icon={<Megaphone size={14} />}
              label={`${count} Total`}
              sx={{
                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                color: '#6C63FF',
                border: '1px solid rgba(108, 99, 255, 0.25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: 34,
                flexShrink: 0,
                '& .MuiChip-icon': { color: '#6C63FF' },
              }}
            />
          </Box>
        </Box>
      </Grid>

      {/* ========== Promo Cards ========== */}
      <Grid item xs={12}>
        <Box className="approved-release-list">
          {promo?.length ? (
            promo.map((item) => renderPromoCard(item))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Promotions Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
            />
          </Box>
        )}
      </Grid>

      {/* Image Preview Modal */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            position: 'relative',
          },
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.85)' } },
        }}
      >
        <IconButton
          onClick={() => setPreviewImage(null)}
          sx={{
            position: 'absolute', top: -16, right: -16, zIndex: 1,
            backgroundColor: 'rgba(30, 33, 48, 0.9)',
            color: '#F1F5F9',
            border: '1px solid #2E3247',
            '&:hover': { backgroundColor: '#2E3247' },
            width: 36, height: 36,
          }}
        >
          <X size={18} />
        </IconButton>
        {previewImage && (
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxWidth: '90vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 8,
              display: 'block',
            }}
          />
        )}
      </Dialog>
    </Grid>
  );
}
